import React, { useState, useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import { getEncoding } from 'js-tiktoken';
import { Sparkles, Trash2, Activity, Award, AlertTriangle, Lightbulb, Hash, FileText, Cpu } from 'lucide-react';

// Lazy encoder load (BPE table is ~1MB, fine in browser, no API calls)
let _encoder = null;
const getEncoder = () => {
  if (!_encoder) _encoder = getEncoding('cl100k_base');
  return _encoder;
};

const SAMPLE = `You are an expert technical recruiter. Review the following resume and identify the top 3 strengths and top 3 risks of the candidate for a Principal Platform Engineer role at a global bank.

Constraints:
- Reply in JSON with keys "strengths" and "risks", each an array of objects with "title" and "rationale".
- Limit each rationale to two sentences.
- Cite at least one resume quote per item.

Resume:
"""
<paste resume here>
"""

Example response:
{"strengths":[{"title":"...","rationale":"...","quote":"..."}], "risks":[...]}`;

// ============ Local heuristic scorer (NO LLM CALLS) ============
const RULES = [
  {
    id: 'clarity',
    label: 'Clarity',
    color: '#00f0ff',
    score: (t) => {
      const words = t.trim().split(/\s+/).filter(Boolean).length;
      const sentences = t.split(/[.!?\n]+/).filter((s) => s.trim().length > 0);
      const avgLen = sentences.length ? words / sentences.length : 0;
      let s = 70;
      if (words < 8) return 25;
      if (words >= 25 && words <= 280) s += 15;
      if (avgLen > 0 && avgLen <= 22) s += 10;
      if (/\b(?:please|kindly|maybe|might|sort of|kind of|stuff|things)\b/i.test(t)) s -= 12;
      if (/\b(?:specifically|exactly|must|should|return|provide|list|generate)\b/i.test(t)) s += 5;
      return Math.max(0, Math.min(100, s));
    },
  },
  {
    id: 'context',
    label: 'Context',
    color: '#39ff14',
    score: (t) => {
      let s = 40;
      if (/(role|act as|you are|persona|background)/i.test(t)) s += 25;
      if (/(audience|reader|user|stakeholder)/i.test(t)) s += 10;
      if (t.length >= 120) s += 10;
      if (t.length >= 350) s += 10;
      if (/(domain|industry|company|product|tone)/i.test(t)) s += 5;
      return Math.min(100, s);
    },
  },
  {
    id: 'constraints',
    label: 'Constraints',
    color: '#9d4edd',
    score: (t) => {
      let s = 35;
      if (/(constraints?:|requirements?:|rules?:|limits?:)/i.test(t)) s += 25;
      const bullets = (t.match(/(^|\n)[\-\*\d]+[\.)]?\s/g) || []).length;
      if (bullets >= 2) s += 15;
      if (bullets >= 4) s += 10;
      if (/(must|should|do not|don't|never|avoid|limit|max|at most|exactly)/i.test(t)) s += 15;
      return Math.min(100, s);
    },
  },
  {
    id: 'format',
    label: 'Output Format',
    color: '#ff9e00',
    score: (t) => {
      let s = 30;
      if (/(json|yaml|markdown|table|csv|xml|bullet|list)/i.test(t)) s += 30;
      if (/\bschema\b|"keys?"|"fields?"|<output>|```/i.test(t)) s += 20;
      if (/(format|structure|template)/i.test(t)) s += 15;
      if (/respond in|reply in|return as|output as/i.test(t)) s += 15;
      return Math.min(100, s);
    },
  },
  {
    id: 'examples',
    label: 'Examples',
    color: '#22d3ee',
    score: (t) => {
      const blocks = (t.match(/```[\s\S]*?```/g) || []).length;
      const exampleMentions = (t.match(/\b(example|e\.g\.|for instance|sample)\b/gi) || []).length;
      let s = 30;
      if (blocks >= 1) s += 30;
      if (blocks >= 2) s += 15;
      if (exampleMentions >= 1) s += 15;
      if (exampleMentions >= 2) s += 10;
      return Math.min(100, s);
    },
  },
  {
    id: 'structure',
    label: 'Structure',
    color: '#f43f5e',
    score: (t) => {
      let s = 35;
      const headings = (t.match(/(^|\n)#{1,3}\s|(^|\n)[A-Z][A-Za-z ]{0,30}:\s*\n/g) || []).length;
      if (headings >= 1) s += 20;
      if (headings >= 3) s += 15;
      const paragraphs = t.split(/\n\s*\n/).length;
      if (paragraphs >= 2) s += 15;
      if (paragraphs >= 4) s += 10;
      if (t.length > 60 && /\n/.test(t)) s += 5;
      return Math.min(100, s);
    },
  },
];

const gradeFor = (n) => {
  if (n >= 92) return 'A+';
  if (n >= 85) return 'A';
  if (n >= 72) return 'B';
  if (n >= 58) return 'C';
  return 'D';
};

const analyzeStrengths = (t, dims) => {
  const s = [];
  if (dims.clarity >= 75) s.push('Direct, action-oriented language with concrete verbs.');
  if (dims.context >= 75) s.push('Persona / role context is well established.');
  if (dims.constraints >= 70) s.push('Explicit constraints and rules reduce ambiguity for the model.');
  if (dims.format >= 70) s.push('Clearly specifies the expected output format / schema.');
  if (dims.examples >= 70) s.push('Includes one or more concrete examples to anchor the model.');
  if (dims.structure >= 70) s.push('Well-structured with clear sections and whitespace.');
  return s.length ? s : ['Your prompt has the basics in place — see Recommendations for ways to elevate it.'];
};
const analyzeIssues = (t, dims) => {
  const i = [];
  if (dims.clarity < 60) i.push('Language feels vague — replace soft words ("maybe", "stuff") with explicit verbs.');
  if (dims.context < 55) i.push('Missing persona / domain context — tell the model who it is and who the reader is.');
  if (dims.constraints < 55) i.push('No clear constraints — add hard limits (length, scope, do/don\'t rules).');
  if (dims.format < 55) i.push('Output format is not specified — request JSON / Markdown / table explicitly.');
  if (dims.examples < 55) i.push('No examples provided — even a single tiny example dramatically boosts quality.');
  if (dims.structure < 55) i.push('Wall of text — split into labelled sections (Role, Context, Task, Format, Examples).');
  if (t.trim().length < 30) i.push('Prompt is very short — under 30 chars usually produces brittle output.');
  return i.length ? i : ['No critical issues detected. Run the prompt and iterate based on output quality.'];
};
const recommendations = (t, dims) => {
  const r = [];
  if (dims.context < 80) r.push('Add a one-line persona: "You are a [role] specialised in [domain]."');
  if (dims.format < 80) r.push('End with "Respond in JSON with keys X, Y, Z" or paste a sample schema.');
  if (dims.examples < 80) r.push('Include a `Example input → expected output` block to anchor the response.');
  if (dims.constraints < 80) r.push('Add a Constraints section with bullet points (length limits, allowed sources, disallowed actions).');
  if (dims.structure < 80) r.push('Use Markdown headings (Role / Task / Constraints / Format / Example) — improves parseability.');
  if (dims.clarity < 80) r.push('Cut filler words. Each sentence should command an action with a measurable outcome.');
  return r;
};

const Card = ({ label, value, sub, color, icon: Icon, testid }) => (
  <div className="glass-card rounded-xl border border-slate-800 p-4 space-y-3" data-testid={testid}>
    <div className="flex items-center justify-between">
      <div className="w-9 h-9 rounded-lg border flex items-center justify-center" style={{ background: `${color}15`, borderColor: `${color}40`, color }}>
        <Icon className="w-4 h-4" />
      </div>
      <span className="font-mono text-[9px] text-slate-500 uppercase tracking-widest">{label}</span>
    </div>
    <div className="font-display font-extrabold text-2xl text-white">{value}</div>
    {sub && <div className="font-mono text-[10px] text-slate-500">{sub}</div>}
  </div>
);

const Bar = ({ label, score, color }) => (
  <div className="space-y-1">
    <div className="flex justify-between font-mono text-[10px] uppercase tracking-widest">
      <span style={{ color }}>{label}</span>
      <span className="text-slate-300">{score}/100</span>
    </div>
    <div className="h-1.5 rounded-full bg-slate-900 overflow-hidden">
      <motion.div initial={{ width: 0 }} animate={{ width: `${score}%` }} transition={{ duration: 0.6, ease: 'easeOut' }} className="h-full rounded-full" style={{ background: color, boxShadow: `0 0 12px ${color}80` }} />
    </div>
  </div>
);

export const PromptAnalyzerView = () => {
  const [text, setText] = useState('');
  const [result, setResult] = useState(null);

  const charCount = text.length;
  const wordCount = useMemo(() => (text.trim() ? text.trim().split(/\s+/).filter(Boolean).length : 0), [text]);

  const handleAnalyze = useCallback(() => {
    const t = text;
    let tokens = 0;
    try {
      tokens = getEncoder().encode(t).length;
    } catch {
      tokens = Math.ceil(charCount / 4);
    }
    // Heuristic output estimate: prompts that ask for JSON / explicit length cap closer to prompt size,
    // open-ended prompts often produce 2-4x.
    const askedJson = /(json|table|schema|format)/i.test(t);
    const askedLimit = /\b(words?|sentences?|tokens?|chars?)\b/i.test(t);
    const factor = askedJson && askedLimit ? 1.4 : askedLimit ? 1.6 : askedJson ? 2.2 : 3.0;
    const outTokens = Math.max(64, Math.round(tokens * factor));

    const dims = {};
    RULES.forEach((r) => { dims[r.id] = r.score(t); });
    const overall = Math.round(RULES.reduce((s, r) => s + dims[r.id] * (r.id === 'clarity' || r.id === 'context' ? 0.22 : 0.14), 0));
    setResult({
      tokens, outTokens, dims,
      overall,
      grade: gradeFor(overall),
      strengths: analyzeStrengths(t, dims),
      issues: analyzeIssues(t, dims),
      recommendations: recommendations(t, dims),
    });
  }, [text, charCount]);

  const handleClear = () => { setText(''); setResult(null); };

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-10" data-testid="prompt-analyzer-view">
      <section className="space-y-2">
        <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#00f0ff]/10 border border-[#00f0ff]/25 rounded-full">
          <Sparkles className="w-3 h-3 text-[#00f0ff]" />
          <span className="font-mono text-[10px] text-[#00f0ff] tracking-widest font-semibold uppercase">05 // FEATURES · PROMPT ANALYZER</span>
        </div>
        <h2 className="font-display font-black text-3xl sm:text-4xl text-white tracking-tight uppercase">
          Prompt <span className="text-[#00f0ff]">Analyzer</span>
        </h2>
        <p className="text-slate-400 text-xs sm:text-sm font-light leading-relaxed max-w-2xl">
          Paste any prompt and get a private, on-device quality breakdown — token count via tiktoken, plus a rule-based score (no LLM, no API cost).
        </p>
      </section>

      <section className="glass-card rounded-xl border border-slate-800 p-5 space-y-4">
        <label className="font-mono text-[10px] uppercase tracking-widest text-[#00f0ff] block">Prompt</label>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Paste your prompt here…"
          rows={12}
          className="w-full px-4 py-3 bg-slate-950 border border-slate-850 font-mono text-xs rounded text-white focus:outline-none focus:border-[#00f0ff] focus:ring-1 focus:ring-[#00f0ff]/40 transition-all placeholder:text-slate-600 resize-y leading-relaxed"
          data-testid="prompt-input"
        />
        <div className="flex flex-wrap items-center gap-3">
          <button onClick={handleAnalyze} disabled={!text.trim()} className="px-5 py-2.5 rounded bg-gradient-to-r from-[#00f0ff] to-[#0070ff] disabled:opacity-40 text-slate-950 font-display font-bold text-xs uppercase tracking-widest cursor-pointer flex items-center gap-2 transition-all hover:scale-[1.02]" data-testid="btn-analyze">
            <Activity className="w-3.5 h-3.5" />
            <span>Analyze</span>
          </button>
          <button onClick={handleClear} className="px-5 py-2.5 rounded border border-slate-800 bg-slate-900 hover:bg-slate-800 text-slate-300 font-mono font-bold text-xs uppercase tracking-widest cursor-pointer flex items-center gap-2" data-testid="btn-clear">
            <Trash2 className="w-3.5 h-3.5" />
            <span>Clear</span>
          </button>
          <button onClick={() => setText(SAMPLE)} className="px-3 py-2 rounded border border-[#9d4edd]/40 bg-[#9d4edd]/10 hover:bg-[#9d4edd]/20 text-[#9d4edd] font-mono font-bold text-[10px] uppercase tracking-widest cursor-pointer" data-testid="btn-sample">
            Load Sample
          </button>
          <div className="ml-auto flex items-center gap-3 font-mono text-[10px] text-slate-400">
            <span>{charCount} chars</span>
            <span>·</span>
            <span>{wordCount} words</span>
          </div>
        </div>
      </section>

      {result && (
        <>
          <section className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="results-counters">
            <Card label="Tokens (cl100k_base)" value={result.tokens} sub="GPT-3.5/4 family" color="#00f0ff" icon={Hash} testid="card-tokens" />
            <Card label="Est. output tokens" value={result.outTokens} sub="heuristic" color="#9d4edd" icon={Cpu} testid="card-out-tokens" />
            <Card label="Characters" value={charCount} color="#39ff14" icon={FileText} testid="card-chars" />
            <Card label="Words" value={wordCount} color="#ff9e00" icon={FileText} testid="card-words" />
          </section>

          <section className="glass-card rounded-xl border border-slate-800 p-6 space-y-5" data-testid="results-score">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#00f0ff]">Quality Score</div>
                <div className="flex items-baseline gap-3">
                  <span className="font-display font-black text-5xl text-white">{result.overall}</span>
                  <span className="font-mono text-sm text-slate-500">/ 100</span>
                </div>
              </div>
              <div className="text-right">
                <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Grade</div>
                <div className="font-display font-black text-5xl" style={{ color: result.overall >= 85 ? '#39ff14' : result.overall >= 72 ? '#00f0ff' : result.overall >= 58 ? '#ff9e00' : '#f43f5e' }}>{result.grade}</div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3 pt-4 border-t border-slate-900">
              {RULES.map((r) => <Bar key={r.id} label={r.label} score={result.dims[r.id]} color={r.color} />)}
            </div>
          </section>

          <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="glass-card rounded-xl border border-emerald-500/25 p-5 space-y-3" data-testid="card-strengths">
              <div className="flex items-center gap-2"><Award className="w-4 h-4 text-[#39ff14]" /><h4 className="font-display font-bold text-sm text-white uppercase">Strengths</h4></div>
              <ul className="space-y-2 text-xs text-slate-300 font-light leading-relaxed list-disc list-inside">
                {result.strengths.map((s, i) => <li key={i}>{s}</li>)}
              </ul>
            </div>
            <div className="glass-card rounded-xl border border-amber-500/25 p-5 space-y-3" data-testid="card-issues">
              <div className="flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-[#ff9e00]" /><h4 className="font-display font-bold text-sm text-white uppercase">Issues</h4></div>
              <ul className="space-y-2 text-xs text-slate-300 font-light leading-relaxed list-disc list-inside">
                {result.issues.map((s, i) => <li key={i}>{s}</li>)}
              </ul>
            </div>
            <div className="glass-card rounded-xl border border-[#00f0ff]/30 p-5 space-y-3" data-testid="card-recommendations">
              <div className="flex items-center gap-2"><Lightbulb className="w-4 h-4 text-[#00f0ff]" /><h4 className="font-display font-bold text-sm text-white uppercase">Recommendations</h4></div>
              <ul className="space-y-2 text-xs text-slate-300 font-light leading-relaxed list-disc list-inside">
                {result.recommendations.length === 0 ? <li>You're at peak prompt-craft. Ship it.</li> : result.recommendations.map((s, i) => <li key={i}>{s}</li>)}
              </ul>
            </div>
          </section>

          <p className="font-mono text-[10px] text-slate-600 text-center tracking-widest uppercase">
            100% local · zero LLM calls · zero API cost
          </p>
        </>
      )}
    </motion.div>
  );
};
